import { IForm } from 'core-components/classes/models/IForm'
import { IQueryResult } from 'core-components/types'
import { Crud } from 'src/core/types/Crud'

interface IWorkerMap {
  fetchFn: () => Promise<IQueryResult<any, any>>
  promise?: Promise<IQueryResult<any, any>>
  resolve: (value: IQueryResult<any, any>) => void
  reject: (reason?: any) => void
}

function saveRecordToStore(
  getState: () => any,
  save: (formId: string, query: string, data: { items: IForm[]; total: number }) => void,
  form: any,
  item: IForm,
  query?: string,
  update?: boolean
) {
  const state = getState()
  const savedQueries = state.forms[form].queries
  save(form, '', { items: [item], total: 0 })
  if (query) {
    for (const i in savedQueries) {
      if (i.indexOf(query) > -1) {
        savedQueries[i] = {
          ...savedQueries[i],
          items: update ? [...savedQueries[i].items] : [...savedQueries[i].items, item],
        }
      }
    }
    state.forms.itemResults.queries = savedQueries
  }
}

class Queue {
  protected queue: Promise<unknown> | undefined
  private writeQueue: {
    promise: () => Promise<any[] | boolean>
    resolve: (value: any[] | boolean) => void
    reject: (reason?: any) => void
  }[] = []

  private readQueue: Map<
    Crud<any, any>, // crud instance key
    {
      workers: Map<
        string, // query key
        IWorkerMap
      >
      activeWorker: {
        promise?: Promise<IQueryResult<any, any>>
      }
    }
  > = new Map()

  private queryCount: number = 0
  private workingOnPromise: boolean = false

  public add(
    getState: () => any,
    save: (formId: string, query: string, data: { items: IForm[]; total: number }) => void,
    initial: IForm,
    waiter: (fakeMyRecord: any) => Promise<IForm>,
    callback: (arg0?: any, arg1?: any) => any,
    form: string,
    query?: string
  ): IForm {
    const fakeRecord = initial
    saveRecordToStore(getState, save, form, fakeRecord, query)
    let oldQuery: Promise<unknown> | undefined
    this.queryCount += 1
    if (this.queue) {
      oldQuery = this.queue
    }
    this.queue = new Promise<void>(async (resolve) => {
      if (oldQuery) {
        await oldQuery
      }
      const savedRecord = await waiter(fakeRecord)
      const state = getState()
      const dataForm: IForm[] = state.forms[form].data
      const editedItem = dataForm.find((el) => {
        return el.EntryId === fakeRecord.EntryId
      })
      const fakeRecordDump = { ...fakeRecord }
      if (editedItem) {
        editedItem.EntryId =
          (savedRecord.items && savedRecord.items[0].EntryId) || savedRecord[0].EntryId || 0
        saveRecordToStore(getState, save, form, editedItem, query, true)
      }
      callback(editedItem, fakeRecordDump)
      this.queue = undefined
      this.queryCount -= 1
      resolve()
    })
    return fakeRecord
  }

  /*
   * Update method adds waiter to queue but doesn't save information
   *
   */
  public update(waiter: () => Promise<IForm>): void {
    let oldQuery: Promise<unknown> | undefined
    if (this.queue) {
      oldQuery = this.queue
    }
    this.queue = new Promise<void>(async (resolve) => {
      if (oldQuery) {
        await oldQuery
      }
      await waiter()
      this.queue = undefined
      resolve()
    })
  }

  public read = async <T = IForm, R = string | number>(
    query: string,
    crudIntance: Crud<T, R>,
    process: () => Promise<IQueryResult<T, R>>
  ): Promise<IQueryResult<T, R>> => {
    return await this.readEnqueue<T, R>(query, process, crudIntance)
  }

  public writeEnqueue = <T>(promise: () => Promise<T[] | boolean>) => {
    return new Promise<T[] | boolean>((resolve, reject) => {
      this.writeQueue.push({
        promise,
        resolve,
        reject,
      })
      this.writeDequeue()
    })
  }

  private writeDequeue = () => {
    if (this.workingOnPromise) {
      return false
    }
    const item = this.writeQueue.shift()
    if (!item) {
      return false
    }
    try {
      this.workingOnPromise = true
      item
        .promise()
        .then((value) => {
          this.workingOnPromise = false
          item.resolve(value)
          this.writeDequeue()
        })
        .catch((err) => {
          this.workingOnPromise = false
          item.reject(err)
          this.writeDequeue()
        })
    } catch (err) {
      this.workingOnPromise = false
      item.reject(err)
      this.writeDequeue()
    }
    return true
  }

  private readEnqueue = async <T, R>(
    query: string,
    fetchFn: () => Promise<IQueryResult<T, R>>,
    crudIntance: Crud<T, R>
  ): Promise<IQueryResult<T, R>> => {
    const currentCrud = this.readQueue.get(crudIntance)
    const currObj = currentCrud || {
      workers: new Map<string, IWorkerMap>(),
      activeWorker: {},
    }
    if (
      currentCrud &&
      currentCrud.workers.get(query) &&
      // @ts-ignore
      currentCrud.workers.get(query).promise
    ) {
      // @ts-ignore
      const res = await currentCrud.workers.get(query).promise
      return this.readEnqueue<T, R>(query, fetchFn, crudIntance)
    }
    let res: any, rej: any
    const promise = new Promise<IQueryResult<T, R>>((resolve, reject) => {
      res = resolve
      rej = reject
    })
    currObj.workers.set(query, { fetchFn, resolve: res, reject: rej, promise })
    this.readQueue.set(crudIntance, currObj)
    this.readDequeue(crudIntance)
    return promise
  }

  private readDequeue = <T, R>(crudIntance: Crud<T, R>) => {
    const currentCrudQueue = this.readQueue.get(crudIntance)
    if (!currentCrudQueue) {
      return
    }
    if (currentCrudQueue.activeWorker.promise) {
      return false
    }
    const item = currentCrudQueue.workers.entries().next().value // get first map entry
    if (!item) {
      return false
    }
    try {
      currentCrudQueue.activeWorker.promise = item[1].fetchFn()
      currentCrudQueue.activeWorker.promise &&
        currentCrudQueue.activeWorker.promise
          .then((value) => {
            currentCrudQueue.activeWorker = {}
            item[1].resolve(value)
            currentCrudQueue.workers.delete(item[0])
            this.readDequeue(crudIntance)
          })
          .catch((err) => {
            currentCrudQueue.activeWorker = {}
            item[1].reject(err)
            currentCrudQueue.workers.delete(item[0])
            this.readDequeue(crudIntance)
          })
    } catch (err) {
      currentCrudQueue.activeWorker = {}
      item[1].reject(err)
      currentCrudQueue.workers.delete(item[0])
      this.readDequeue(crudIntance)
    }
    return true
  }
}

export default new Queue()
